import { kml as kmlToGeoJson } from '@tmcw/togeojson';
import { sanitizeDocumentHtml } from '../../utils/documentHtml';

/**
 * @tmcw/togeojson does not resolve <StyleMap> indirections — it silently produces no style
 * properties for any Placemark whose <styleUrl> points to a <StyleMap> instead of a <Style>.
 * This function rewrites those references in-place on the DOM to the StyleMap's "normal" pair
 * before togeojson processes it, so icon/color/stroke properties are correctly picked up.
 */
function resolveStyleMaps(dom: Document): void {
  const styleMapEls = dom.getElementsByTagName('StyleMap');
  const styleMaps = new Map<string, string>();

  for (let i = 0; i < styleMapEls.length; i++) {
    const styleMap = styleMapEls[i];
    const id = styleMap.getAttribute('id');
    if (!id) continue;
    const pairs = styleMap.getElementsByTagName('Pair');
    for (let j = 0; j < pairs.length; j++) {
      const pair = pairs[j];
      const key = pair.getElementsByTagName('key')[0]?.textContent?.trim();
      if (key === 'normal') {
        const styleUrl = pair.getElementsByTagName('styleUrl')[0]?.textContent?.trim();
        if (styleUrl) styleMaps.set(`#${id}`, styleUrl);
        break;
      }
    }
  }

  if (styleMaps.size === 0) return;

  const styleUrlEls = dom.getElementsByTagName('styleUrl');
  for (let i = 0; i < styleUrlEls.length; i++) {
    const el = styleUrlEls[i];
    const ref = el.textContent?.trim();
    if (ref && styleMaps.has(ref)) {
      el.textContent = styleMaps.get(ref)!;
    }
  }
}

/**
 * For Point Placemarks that carry a <LookAt><range> value, injects that value into
 * <ExtendedData> so @tmcw/togeojson surfaces it as a GeoJSON feature property named
 * "lookAtRange". A small range (close-up camera) signals a detail-level feature that
 * KmlLayer hides until the user zooms in; MapDocPreviewer replicates that via zoom_changed.
 */
function annotatePointsWithLookAtRange(dom: Document): void {
  const placemarks = dom.getElementsByTagName('Placemark');
  for (let i = 0; i < placemarks.length; i++) {
    const placemark = placemarks[i];
    if (!placemark.getElementsByTagName('Point').length) continue;
    const lookAtEl = placemark.getElementsByTagName('LookAt')[0];
    if (!lookAtEl) continue;
    const rangeText = lookAtEl.getElementsByTagName('range')[0]?.textContent?.trim();
    if (!rangeText) continue;
    const range = parseFloat(rangeText);
    if (isNaN(range)) continue;

    let extData = placemark.getElementsByTagName('ExtendedData')[0];
    if (!extData) {
      extData = dom.createElement('ExtendedData');
      placemark.appendChild(extData);
    }
    const dataEl = dom.createElement('Data');
    dataEl.setAttribute('name', 'lookAtRange');
    const valueEl = dom.createElement('value');
    valueEl.textContent = String(range);
    dataEl.appendChild(valueEl);
    extData.appendChild(dataEl);
  }
}

/** The "lookAtRange" annotatePointsWithLookAtRange gives a Point, in metres, or NaN for a feature without one. */
export const lookAtRangeOf = (feature: google.maps.Data.Feature): number => {
  const range = feature.getProperty('lookAtRange');
  // togeojson turns a file's own SimpleData of a numeric schema type into a number.
  return typeof range === 'number' ? range : parseFloat(propertyText(range));
};

/**
 * Resolves and injects <BalloonStyle><text> content for each Placemark.
 * @tmcw/togeojson ignores BalloonStyle entirely — it only extracts <description>.
 * This pre-pass:
 *   1. Builds a map of style id → balloon text from all <Style> elements.
 *   2. Resolves each Placemark's <styleUrl> to its balloon text.
 *   3. Substitutes $[name] and $[description] template variables with the
 *      Placemark's actual values (matching KmlLayer behaviour).
 *   4. Injects the result as a "balloonText" ExtendedData property so
 *      @tmcw/togeojson surfaces it in GeoJSON feature properties.
 * The text goes in as the file wrote it: a file can set balloonText itself, so parseKml sanitizes it with the
 * description once togeojson has read every source of both.
 */
function annotateBalloonStyles(dom: Document): void {
  // Build style id → balloon text map
  const balloonMap = new Map<string, string>();
  const styleEls = dom.getElementsByTagName('Style');
  for (let i = 0; i < styleEls.length; i++) {
    const style = styleEls[i];
    const id = style.getAttribute('id');
    if (!id) continue;
    const balloonEl = style.getElementsByTagName('BalloonStyle')[0];
    if (!balloonEl) continue;
    const textEl = balloonEl.getElementsByTagName('text')[0];
    if (!textEl) continue;
    const text = textEl.textContent?.trim();
    if (text) balloonMap.set(`#${id}`, text);
  }

  if (balloonMap.size === 0) return;

  const placemarks = dom.getElementsByTagName('Placemark');
  for (let i = 0; i < placemarks.length; i++) {
    const placemark = placemarks[i];
    const styleUrlEl = placemark.getElementsByTagName('styleUrl')[0];
    if (!styleUrlEl) continue;
    const styleRef = styleUrlEl.textContent?.trim();
    if (!styleRef || !balloonMap.has(styleRef)) continue;

    let balloon = balloonMap.get(styleRef)!;

    // Substitute $[name] and $[description] template variables
    const name = placemark.getElementsByTagName('name')[0]?.textContent?.trim() ?? '';
    const description = placemark.getElementsByTagName('description')[0]?.textContent?.trim() ?? '';
    balloon = balloon.replace(/\$\[name\]/g, name).replace(/\$\[description\]/g, description);

    let extData = placemark.getElementsByTagName('ExtendedData')[0];
    if (!extData) {
      extData = dom.createElement('ExtendedData');
      placemark.appendChild(extData);
    }
    const dataEl = dom.createElement('Data');
    dataEl.setAttribute('name', 'balloonText');
    const valueEl = dom.createElement('value');
    valueEl.textContent = balloon;
    dataEl.appendChild(valueEl);
    extData.appendChild(dataEl);
  }
}

/**
 * Base icon URL and tint colour used for Point features that carry no valid
 * <IconStyle><href>. The white circle paddle is tinted via the same API used for
 * all other icon colouring, so no special rendering path is needed.
 * `collectIconTintRequests` automatically emits a tint request for this combination
 * whenever such a feature exists in the parsed GeoJSON.
 */
export const DEFAULT_POINT_ICON_URL = 'https://maps.google.com/mapfiles/kml/paddle/wht-circle.png';
export const DEFAULT_POINT_ICON_COLOR = '#5880fb';

/**
 * Stable tint-map key for the default Point icon. Used by both
 * `collectIconTintRequests` (to request the tint) and `applyKmlStyles` (to look it up).
 */
export const DEFAULT_POINT_ICON_KEY = `${DEFAULT_POINT_ICON_URL}|${DEFAULT_POINT_ICON_COLOR.replace('#', '')}|1.0000`;

/**
 * For line/polygon Placemarks whose resolved style defines a <LineStyle> with a <width>
 * but no <color>, injects a "folderPaletteColor" ExtendedData entry that approximates
 * KmlLayer's per-folder cycling colour behaviour.
 *
 * KmlLayer assigns different default colours to unstyled lines depending on which
 * <Folder> contains them. We approximate this by indexing a palette by the Placemark's
 * ancestor Folder nesting depth: palette[depth % palette.length]. The two anchor points
 * are calibrated from observed KmlLayer output on doc.kml 2:
 *   nesting depth 4 → #8a79ee (medium violet), nesting depth 5 → #00ffff (teal/cyan).
 *
 * Must run after resolveStyleMaps (styleUrl references already resolved to style IDs)
 * and after annotateExtendedDataKeys (so the internal key is not surfaced in popup tables).
 */
function annotateLineFolderColors(dom: Document): void {
  // Build set of style IDs that have a <LineStyle> with <width> but no <color>.
  const colorlessLineStyleIds = new Set<string>();
  const styleEls = dom.getElementsByTagName('Style');
  for (let i = 0; i < styleEls.length; i++) {
    const style = styleEls[i];
    const id = style.getAttribute('id');
    if (!id) continue;
    const lineStyleEl = style.getElementsByTagName('LineStyle')[0];
    if (!lineStyleEl) continue;
    if (lineStyleEl.getElementsByTagName('width')[0] && !lineStyleEl.getElementsByTagName('color')[0]) {
      colorlessLineStyleIds.add(`#${id}`);
    }
  }
  if (colorlessLineStyleIds.size === 0) return;

  // Palette indexed by folder nesting depth (number of ancestor <Folder> elements).
  // Index 4 = #8a79ee and index 5 = #00ffff are empirically calibrated from KmlLayer.
  const FOLDER_DEPTH_PALETTE = ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#8a79ee', '#00ffff'];

  // Assign each <Folder> element a palette colour based on nesting depth (DFS traversal).
  const folderColorMap = new Map<Element, string>();
  const assignColors = (node: Element, folderDepth: number): void => {
    const children = node.children;
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      if (child.tagName === 'Folder') {
        const newDepth = folderDepth + 1;
        folderColorMap.set(child, FOLDER_DEPTH_PALETTE[newDepth % FOLDER_DEPTH_PALETTE.length]);
        assignColors(child, newDepth);
      } else {
        assignColors(child, folderDepth);
      }
    }
  };
  assignColors(dom.documentElement, 0);

  // Inject folderPaletteColor into each qualifying line Placemark.
  const placemarks = dom.getElementsByTagName('Placemark');
  for (let i = 0; i < placemarks.length; i++) {
    const placemark = placemarks[i];

    const hasLineGeometry =
      placemark.getElementsByTagName('LineString').length > 0 ||
      placemark.getElementsByTagName('Polygon').length > 0 ||
      placemark.getElementsByTagName('LinearRing').length > 0;
    if (!hasLineGeometry) continue;

    const styleUrlEl = placemark.getElementsByTagName('styleUrl')[0];
    if (!styleUrlEl) continue;
    const styleRef = styleUrlEl.textContent?.trim();
    if (!styleRef || !colorlessLineStyleIds.has(styleRef)) continue;

    // Walk up to the nearest ancestor <Folder> with an assigned palette colour.
    let ancestor: Element | null = placemark.parentElement;
    let paletteColor: string | undefined;
    while (ancestor && ancestor !== dom.documentElement) {
      if (ancestor.tagName === 'Folder') {
        paletteColor = folderColorMap.get(ancestor);
        if (paletteColor) break;
      }
      ancestor = ancestor.parentElement;
    }
    if (!paletteColor) continue;

    let extData = placemark.getElementsByTagName('ExtendedData')[0];
    if (!extData) {
      extData = dom.createElement('ExtendedData');
      placemark.appendChild(extData);
    }
    const dataEl = dom.createElement('Data');
    dataEl.setAttribute('name', 'folderPaletteColor');
    const valueEl = dom.createElement('value');
    valueEl.textContent = paletteColor;
    dataEl.appendChild(valueEl);
    extData.appendChild(dataEl);
  }
}

/**
 * Records which property keys on each Placemark originated from <ExtendedData><Data>
 * elements. After @tmcw/togeojson flattens everything into GeoJSON properties there is no
 * way to tell ExtendedData fields apart from style fields, so we inject an
 * "extendedDataKeys" Data element (JSON array of names) before conversion runs.
 * The click handler uses this list to show only user-defined data in the popup table.
 */
function annotateExtendedDataKeys(dom: Document): void {
  const placemarks = dom.getElementsByTagName('Placemark');
  for (let i = 0; i < placemarks.length; i++) {
    const placemark = placemarks[i];
    const extData = placemark.getElementsByTagName('ExtendedData')[0];
    if (!extData) continue;
    const dataEls = extData.getElementsByTagName('Data');
    if (dataEls.length === 0) continue;
    const keys: string[] = [];
    for (let j = 0; j < dataEls.length; j++) {
      const name = dataEls[j].getAttribute('name');
      if (name) keys.push(name);
    }
    if (keys.length === 0) continue;
    const dataEl = dom.createElement('Data');
    dataEl.setAttribute('name', 'extendedDataKeys');
    const valueEl = dom.createElement('value');
    valueEl.textContent = JSON.stringify(keys);
    dataEl.appendChild(valueEl);
    extData.appendChild(dataEl);
  }
}

export interface IconTintRequest {
  /** Stable lookup key: "<iconUrl>|<RRGGBB>|<opacity>" */
  key: string;
  iconUrl: string;
  hexColor: string;
  opacity: number;
}

/**
 * Walks a parsed GeoJSON FeatureCollection and returns one IconTintRequest per unique
 * (iconUrl, color, opacity) combination that requires server-side tinting.
 *
 * White icons (#ffffff) are excluded as a multiply-tint with white is an identity.
 * Pure data extraction — no network calls, no side effects.
 */
export function collectIconTintRequests(geoJson: ReturnType<typeof kmlToGeoJson>): IconTintRequest[] {
  const seen = new Set<string>();
  const requests: IconTintRequest[] = [];
  let needsDefaultIcon = false;

  for (const feature of geoJson.features) {
    const p = (feature.properties ?? {}) as Record<string, unknown>;
    const iconUrl = p['icon'] as string | undefined;
    const iconColor = p['icon-color'] as string | undefined;
    const iconOpacity = (p['icon-opacity'] as number | undefined) ?? 1;

    // Track whether any Point feature lacks a valid absolute icon URL — those will
    // fall back to DEFAULT_POINT_ICON_URL and need its tint pre-fetched.
    if (feature.geometry?.type === 'Point' && (!iconUrl || !/^(https?)/i.test(iconUrl))) {
      needsDefaultIcon = true;
    }

    if (!iconUrl || !iconColor || iconColor === '#ffffff' || !/^(https?)/i.test(iconUrl)) continue;

    const hexColor = iconColor.replace('#', '');
    const key = `${iconUrl}|${hexColor}|${iconOpacity.toFixed(4)}`;
    if (!seen.has(key)) {
      seen.add(key);
      requests.push({ key, iconUrl, hexColor, opacity: iconOpacity });
    }
  }

  if (needsDefaultIcon && !seen.has(DEFAULT_POINT_ICON_KEY)) {
    requests.push({
      key: DEFAULT_POINT_ICON_KEY,
      iconUrl: DEFAULT_POINT_ICON_URL,
      hexColor: DEFAULT_POINT_ICON_COLOR.replace('#', ''),
      opacity: 1
    });
  }

  return requests;
}

/**
 * Parses a KML string into a GeoJSON FeatureCollection, applying DOM pre-passes to
 * work around @tmcw/togeojson limitations (StyleMap resolution, LookAt range annotation).
 *
 * @param embeddedFiles - Optional map of KMZ-relative paths (e.g. "files/placemark.png")
 *   to blob URLs created from the extracted KMZ archive contents. When provided, any
 *   matching relative <href> inside <IconStyle><Icon> or <ItemIcon> is rewritten to the
 *   blob URL before togeojson runs, so embedded icons are resolved correctly.
 */
export function parseKml(
  kmlText: string,
  embeddedFiles?: ReadonlyMap<string, string>
): ReturnType<typeof kmlToGeoJson> {
  const dom = new DOMParser().parseFromString(kmlText, 'text/xml');
  resolveStyleMaps(dom);
  if (embeddedFiles && embeddedFiles.size > 0) resolveEmbeddedFileHrefs(dom, embeddedFiles);
  annotateExtendedDataKeys(dom);
  annotateBalloonStyles(dom);
  annotatePointsWithLookAtRange(dom);
  annotateLineFolderColors(dom);
  const geoJson = kmlToGeoJson(dom);
  sanitizeBodies(geoJson);
  return geoJson;
}

// The properties a balloon shows as markup; a file can set either itself, through <Data> or <SimpleData>.
const BODY_PROPERTIES = ['balloonText', 'description'];

// Every feature leaves the parse with its bodies sanitized, so no reader of the features is handed the file's markup.
function sanitizeBodies(geoJson: ReturnType<typeof kmlToGeoJson>): void {
  for (const feature of geoJson.features) {
    const properties = (feature.properties ?? {}) as Record<string, unknown>;
    BODY_PROPERTIES.filter((key) => key in properties).forEach((key) => {
      const body = propertyText(properties[key]);
      // A value that is not text, such as a number from a typed <SimpleField>, holds no markup to sanitize.
      if (!body) return;
      // Text with no tag in it holds no markup, which spares most plain descriptions a sanitizing pass each.
      properties[key] = body.includes('<') ? sanitizeDocumentHtml(body) : body;
    });
  }
}

/**
 * Rewrites relative <href> references inside the KML DOM to blob URLs extracted from
 * the enclosing KMZ archive. Handles <IconStyle><Icon><href> and <ItemIcon><href>.
 * Must run after resolveStyleMaps (so style IDs are already normalised) but before
 * annotateBalloonStyles (which reads style elements) and togeojson.
 */
function resolveEmbeddedFileHrefs(dom: Document, embeddedFiles: ReadonlyMap<string, string>): void {
  const hrefEls = dom.getElementsByTagName('href');
  for (let i = 0; i < hrefEls.length; i++) {
    const href = hrefEls[i];
    const path = href.textContent?.trim();
    if (!path) continue;
    // Only rewrite relative paths — absolute http/https/blob/data URLs are already resolved.
    if (/^https?:\/\//i.test(path) || /^(blob:|data:)/i.test(path)) continue;
    const blobUrl = embeddedFiles.get(path);
    if (blobUrl) href.textContent = blobUrl;
  }
}

/**
 * Converts an unknown GeoJSON feature property value to a plain string.
 * @tmcw/togeojson stores CDATA <description> blocks as `{ "@type": "html", value: "..." }`;
 * all other values are expected to be primitive strings.
 */
export function propertyText(val: unknown): string {
  if (typeof val === 'string') return val;
  if (val && typeof val === 'object' && (val as Record<string, unknown>)['@type'] === 'html') {
    const v = (val as Record<string, unknown>)['value'];
    return typeof v === 'string' ? v : '';
  }
  return '';
}

export interface PointCandidate {
  id: number;
  lng: number;
  /** True when the feature has a LookAt range ≤ 800 m (detail-level, hidden at low zoom). */
  constrained: boolean;
}

/**
 * Selects the easternmost and westernmost Point feature IDs from a candidate list,
 * preferring unconstrained (always-visible) features over constrained (detail-level) ones.
 * Returns undefined when the list is empty.
 * Used by MapDocPreviewer to limit the small preview to at most two representative icons.
 */
export function selectPreviewPointIds(allPoints: PointCandidate[]): ReadonlySet<number> | undefined {
  if (allPoints.length === 0) return undefined;

  const sorted = [...allPoints].sort((a, b) => a.lng - b.lng);
  let westPick = sorted[0];
  let eastPick = sorted[sorted.length - 1];

  // Walk inward from each side. Replace a pick only when it is constrained,
  // the incoming candidate is unconstrained, and it is not the other side's
  // current pick. Stop a side once its pick is unconstrained.
  let lo = 1;
  let hi = sorted.length - 2;
  while ((westPick.constrained || eastPick.constrained) && lo <= hi) {
    if (westPick.constrained && lo <= hi) {
      const candidate = sorted[lo++];
      if (!candidate.constrained && candidate.id !== eastPick.id) westPick = candidate;
    }
    if (eastPick.constrained && lo <= hi) {
      const candidate = sorted[hi--];
      if (!candidate.constrained && candidate.id !== westPick.id) eastPick = candidate;
    }
  }

  const ids = new Set<number>();
  ids.add(westPick.id);
  if (eastPick.id !== westPick.id) ids.add(eastPick.id);
  return ids;
}
